import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_TRIES = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const [amount, setAmount] = useState(null);

  const poll = useCallback(async (tries = 0) => {
    if (!sessionId) { setState("error"); return; }
    if (tries >= MAX_TRIES) { setState("timeout"); return; }
    try {
      const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
      setAmount(data.amount);
      if (data.payment_status === "paid") { setState("paid"); return; }
      if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      setTimeout(() => poll(tries + 1), 2000);
    } catch {
      setTimeout(() => poll(tries + 1), 2000);
    }
  }, [sessionId]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-5">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center max-w-lg"
        data-testid="payment-success-page"
      >
        {state === "polling" && (
          <>
            <Loader2 className="animate-spin text-green mx-auto mb-6" size={48} />
            <h1 className="font-display uppercase text-4xl text-white">Confirming your gift…</h1>
            <p className="font-sub text-zinc-400 mt-3">Hang tight while we confirm with Stripe.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="text-green mx-auto mb-6 glow-green" size={64} />
            <h1 className="font-display uppercase text-5xl text-white">Thank you</h1>
            <p className="font-sub text-xl text-zinc-300 mt-4">
              {amount ? `Your $${amount} gift` : "Your gift"} just became meals, water and warmth on the streets.
            </p>
            <p className="font-sub text-zinc-500 mt-2">Saving one soul at a time — with you.</p>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <XCircle className="text-zinc-500 mx-auto mb-6" size={56} />
            <h1 className="font-display uppercase text-4xl text-white">
              {state === "timeout" ? "Still processing" : "Something went wrong"}
            </h1>
            <p className="font-sub text-zinc-400 mt-3">
              {state === "timeout"
                ? "Your payment is taking a moment. If you were charged, your gift is safe."
                : "We couldn't confirm this donation. If you were charged, please contact us."}
            </p>
          </>
        )}
        <Link
          to="/"
          data-testid="back-home"
          className="btn-fill inline-flex items-center gap-2 mt-10 border border-green text-green font-sub font-semibold px-8 py-3.5 rounded-full"
        >
          <ArrowLeft size={18} /> Back home
        </Link>
      </motion.div>
    </div>
  );
}
